import { useMemo } from 'react'
import {
  AnalyticsMetric,
  AnalyticsNamedCounterName,
  LocationTypes,
  useFindAnalyticsLocationsQuery,
  useGetAnalyticsGameAggregateQuery,
  useGetAnalyticsHour2AggregatesQuery,
  useGetAnalyticsLocationAggregatesQuery,
  useGetAnalyticsNamedCounterAggregatesQuery,
} from '../../../../generated/graphql'
import useGame from '../../../../hooks/useGame'

const ANALYTICS_STALE_TIME_MS = 15 * 60 * 1000

const QUERY_OPTIONS = {
  staleTime: ANALYTICS_STALE_TIME_MS,
} as const

const DAY_BUCKETS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const
const TAG_TYPE_BUCKETS = ['app', 'physical'] as const
const HOUR_2_BUCKETS = [
  '00-01',
  '02-03',
  '04-05',
  '06-07',
  '08-09',
  '10-11',
  '12-13',
  '14-15',
  '16-17',
  '18-19',
  '20-21',
  '22-23',
] as const
const MOVEMENT_TYPES = [
  'walking',
  'cycling',
  'rollers',
  'rolling',
  'scooting',
  'jogging',
  'skateboarding',
] as const

interface NamedCounterResult {
  bucketName: string
  value: number
}

export interface CountRow {
  label: string
  value: number
}

export interface LocationSwipeRow {
  description?: string | null
  id: string
  name: string
  slug: string
  swipes: number
}

export interface MovementStats {
  cycling: number
  jogging: number
  rollers: number
  rolling: number
  scooting: number
  skateboarding: number
  total: number
  walking: number
}

export interface HeadlineStats {
  adultPlayers: number
  anonymisedPlayers: number
  beatBoxesTotal: number
  beatBoxesVisited: number
  cardsUsed: number
  childPlayers: number
  femaleAdultPlayers: number
  currentPlayers: number
  joinedPlayers: number
  mileage: number
  newsletterConsent: number
  partnerConsent: number
  registeredDevices: number
  registeredPlayers: number
  surveysCompleted: number
  totalSwipes: number
  unregisteredPlayers: number
}

export interface DemographicHeadline {
  adultInactivity: number
  allSurveyResponses: number
  bame: number
  childInactivity: number
  children: number
  condition: number
  disability: number
  female: number
  inactiveAdult: number
  lessActiveChild: number
  registeredAdults: number
  registeredPlayers: number
}

function getNamedCounterValue(
  counters: NamedCounterResult[] | undefined,
  bucketName: string
): number {
  return (
    counters?.find((counter) => counter.bucketName === bucketName)?.value ?? 0
  )
}

function buildLocationCountMap(
  aggregates: Array<{ count: number; locationId: string }> | undefined
): Record<string, number> {
  return (aggregates ?? []).reduce<Record<string, number>>((acc, aggregate) => {
    acc[aggregate.locationId] = aggregate.count
    return acc
  }, {})
}

export function useCountersAnalyticsData() {
  const game = useGame()
  const gameId = game?.id ?? ''
  const hasGameId = Boolean(gameId)

  const swipesQuery = useGetAnalyticsGameAggregateQuery(
    { gameId, metric: AnalyticsMetric.Swp },
    { enabled: hasGameId, ...QUERY_OPTIONS }
  )
  const joinCountersQuery = useGetAnalyticsNamedCounterAggregatesQuery(
    {
      bucketNames: ['total'],
      counterName: AnalyticsNamedCounterName.Join,
      gameId,
    },
    { enabled: hasGameId, ...QUERY_OPTIONS }
  )
  const registrationCountersQuery = useGetAnalyticsNamedCounterAggregatesQuery(
    {
      bucketNames: ['total', 'child', 'adult'],
      counterName: AnalyticsNamedCounterName.Reg,
      gameId,
    },
    { enabled: hasGameId, ...QUERY_OPTIONS }
  )
  const surveyCountersQuery = useGetAnalyticsNamedCounterAggregatesQuery(
    {
      bucketNames: ['tellUsAboutYou'],
      counterName: AnalyticsNamedCounterName.Surveys,
      gameId,
    },
    { enabled: hasGameId, ...QUERY_OPTIONS }
  )
  const dayCountersQuery = useGetAnalyticsNamedCounterAggregatesQuery(
    {
      bucketNames: [...DAY_BUCKETS],
      counterName: AnalyticsNamedCounterName.Dow,
      gameId,
    },
    { enabled: hasGameId, ...QUERY_OPTIONS }
  )
  const tagTypeCountersQuery = useGetAnalyticsNamedCounterAggregatesQuery(
    {
      bucketNames: [...TAG_TYPE_BUCKETS],
      counterName: AnalyticsNamedCounterName.SwpTagType,
      gameId,
    },
    { enabled: hasGameId, ...QUERY_OPTIONS }
  )
  const mileageCountersQuery = useGetAnalyticsNamedCounterAggregatesQuery(
    {
      bucketNames: ['total'],
      counterName: AnalyticsNamedCounterName.Mileage,
      gameId,
    },
    { enabled: hasGameId, ...QUERY_OPTIONS }
  )
  const cardsCountersQuery = useGetAnalyticsNamedCounterAggregatesQuery(
    {
      bucketNames: ['used'],
      counterName: AnalyticsNamedCounterName.Cards,
      gameId,
    },
    { enabled: hasGameId, ...QUERY_OPTIONS }
  )
  const consentCountersQuery = useGetAnalyticsNamedCounterAggregatesQuery(
    {
      bucketNames: ['newsletter', 'partner'],
      counterName: AnalyticsNamedCounterName.Consent,
      gameId,
    },
    { enabled: hasGameId, ...QUERY_OPTIONS }
  )
  const deviceCountersQuery = useGetAnalyticsNamedCounterAggregatesQuery(
    {
      bucketNames: ['registered'],
      counterName: AnalyticsNamedCounterName.Devices,
      gameId,
    },
    { enabled: hasGameId, ...QUERY_OPTIONS }
  )
  const movementCountersQuery = useGetAnalyticsNamedCounterAggregatesQuery(
    {
      bucketNames: [...MOVEMENT_TYPES],
      counterName: AnalyticsNamedCounterName.Movement,
      gameId,
    },
    { enabled: hasGameId, ...QUERY_OPTIONS }
  )
  const hour2CountersQuery = useGetAnalyticsHour2AggregatesQuery(
    {
      gameId,
      hour2Buckets: [...HOUR_2_BUCKETS],
      metric: AnalyticsMetric.Swp,
    },
    { enabled: hasGameId, ...QUERY_OPTIONS }
  )
  const locationsQuery = useFindAnalyticsLocationsQuery(
    { gameId },
    { enabled: hasGameId, ...QUERY_OPTIONS }
  )
  const locationIds = useMemo(
    () =>
      locationsQuery.data?.findLocations.map((location) => location.id) ?? [],
    [locationsQuery.data?.findLocations]
  )
  const locationCountersQuery = useGetAnalyticsLocationAggregatesQuery(
    {
      gameId,
      locationIds,
      metric: AnalyticsMetric.Swp,
    },
    { enabled: hasGameId && locationIds.length > 0, ...QUERY_OPTIONS }
  )

  const joinCounters = joinCountersQuery.data?.namedCounterAggregates
  const registrationCounters =
    registrationCountersQuery.data?.namedCounterAggregates
  const surveyCounters = surveyCountersQuery.data?.namedCounterAggregates
  const dayCounters = dayCountersQuery.data?.namedCounterAggregates
  const tagTypeCounters = tagTypeCountersQuery.data?.namedCounterAggregates
  const mileageCounters = mileageCountersQuery.data?.namedCounterAggregates
  const cardsCounters = cardsCountersQuery.data?.namedCounterAggregates
  const consentCounters = consentCountersQuery.data?.namedCounterAggregates
  const deviceCounters = deviceCountersQuery.data?.namedCounterAggregates
  const movementCounters = movementCountersQuery.data?.namedCounterAggregates

  const locationRows = useMemo<LocationSwipeRow[]>(() => {
    const countByLocationId = buildLocationCountMap(
      locationCountersQuery.data?.locationAggregates
    )
    return (locationsQuery.data?.findLocations ?? [])
      .map((location) => ({
        description: location.description,
        id: location.id,
        name: location.name ?? location.slug ?? location.id,
        slug:
          location.slug ?? location.beatBoxNumber?.toString() ?? location.id,
        swipes: countByLocationId[location.id] ?? 0,
      }))
      .sort((left, right) => right.swipes - left.swipes)
  }, [
    locationCountersQuery.data?.locationAggregates,
    locationsQuery.data?.findLocations,
  ])

  const headlineStats = useMemo<HeadlineStats>(() => {
    const currentPlayers = getNamedCounterValue(joinCounters, 'total')
    const joinedPlayers = currentPlayers
    const registeredPlayers = getNamedCounterValue(
      registrationCounters,
      'total'
    )
    const childPlayers = getNamedCounterValue(registrationCounters, 'child')
    const adultPlayers = getNamedCounterValue(registrationCounters, 'adult')
    const countByLocationId = buildLocationCountMap(
      locationCountersQuery.data?.locationAggregates
    )
    const beatBoxLocations = (locationsQuery.data?.findLocations ?? []).filter(
      (location) => location.type === LocationTypes.BeatBox && !location.isBonus
    )

    return {
      adultPlayers,
      anonymisedPlayers: 0,
      beatBoxesTotal: beatBoxLocations.length,
      beatBoxesVisited: beatBoxLocations.filter(
        (location) => (countByLocationId[location.id] ?? 0) > 0
      ).length,
      cardsUsed: getNamedCounterValue(cardsCounters, 'used'),
      childPlayers,
      currentPlayers,
      femaleAdultPlayers: 0,
      joinedPlayers,
      mileage: getNamedCounterValue(mileageCounters, 'total'),
      newsletterConsent: getNamedCounterValue(consentCounters, 'newsletter'),
      partnerConsent: getNamedCounterValue(consentCounters, 'partner'),
      registeredDevices: getNamedCounterValue(deviceCounters, 'registered'),
      registeredPlayers,
      surveysCompleted: getNamedCounterValue(surveyCounters, 'tellUsAboutYou'),
      totalSwipes: swipesQuery.data?.gameAggregates?.count ?? 0,
      unregisteredPlayers: Math.max(0, currentPlayers - registeredPlayers),
    }
  }, [
    cardsCounters,
    consentCounters,
    deviceCounters,
    joinCounters,
    locationCountersQuery.data?.locationAggregates,
    locationsQuery.data?.findLocations,
    mileageCounters,
    registrationCounters,
    surveyCounters,
    swipesQuery.data?.gameAggregates?.count,
  ])

  const dayRows = useMemo<CountRow[]>(
    () =>
      DAY_BUCKETS.map((bucket) => ({
        label: bucket,
        value: getNamedCounterValue(dayCounters, bucket),
      })),
    [dayCounters]
  )

  const hour2Rows = useMemo<CountRow[]>(
    () =>
      HOUR_2_BUCKETS.map((bucket) => ({
        label: bucket,
        value:
          hour2CountersQuery.data?.hour2Aggregates.find(
            (aggregate) => aggregate.hour2Bucket === bucket
          )?.count ?? 0,
      })),
    [hour2CountersQuery.data?.hour2Aggregates]
  )

  const tagTypeRows = useMemo<CountRow[]>(
    () =>
      TAG_TYPE_BUCKETS.map((bucket) => ({
        label: bucket,
        value: getNamedCounterValue(tagTypeCounters, bucket),
      })),
    [tagTypeCounters]
  )

  const movementStats = useMemo<MovementStats>(() => {
    const stats: MovementStats = {
      cycling: getNamedCounterValue(movementCounters, 'cycling'),
      jogging: getNamedCounterValue(movementCounters, 'jogging'),
      rollers: getNamedCounterValue(movementCounters, 'rollers'),
      rolling: getNamedCounterValue(movementCounters, 'rolling'),
      scooting: getNamedCounterValue(movementCounters, 'scooting'),
      skateboarding: getNamedCounterValue(movementCounters, 'skateboarding'),
      total: 0,
      walking: getNamedCounterValue(movementCounters, 'walking'),
    }
    stats.total =
      stats.cycling +
      stats.jogging +
      stats.rollers +
      stats.rolling +
      stats.scooting +
      stats.skateboarding +
      stats.walking
    return stats
  }, [movementCounters])

  const demographicHeadline = useMemo<DemographicHeadline>(
    () => ({
      adultInactivity: 0,
      allSurveyResponses: headlineStats.surveysCompleted,
      bame: 0,
      childInactivity: 0,
      children: headlineStats.childPlayers,
      condition: 0,
      disability: 0,
      female: 0,
      inactiveAdult: 0,
      lessActiveChild: 0,
      registeredAdults: headlineStats.adultPlayers,
      registeredPlayers: headlineStats.registeredPlayers,
    }),
    [headlineStats]
  )

  const queries = [
    swipesQuery,
    joinCountersQuery,
    registrationCountersQuery,
    surveyCountersQuery,
    dayCountersQuery,
    tagTypeCountersQuery,
    mileageCountersQuery,
    cardsCountersQuery,
    consentCountersQuery,
    deviceCountersQuery,
    movementCountersQuery,
    hour2CountersQuery,
    locationsQuery,
    locationCountersQuery,
  ]

  return {
    dayRows,
    demographicHeadline,
    demographicRows: [] as CountRow[],
    game,
    hasLocations: locationIds.length > 0,
    headlineStats,
    hour2Rows,
    isLoading: queries.some((query) => query.isLoading),
    locationRows,
    movementStats,
    playerSplitRows: [] as CountRow[],
    surveyRows: [] as CountRow[],
    tagTypeRows,
  }
}
